import { GENERATED_CLUB_CATALOG } from './clubCatalog.generated'
import { CLUB_SEASON_RESULTS } from './clubPerformance'
import { getStageBossBalance } from './constants'
import type { BossCompetition, BossState, FinaleKind, LeagueMovement } from './types'
import { getWorldCup2026Rivals } from './worldCup2026'

export interface ClubDefinition {
  id: string
  name: string
  country: string
  league: string
  division: 1 | 2
  crest: string
}

export interface CareerBossDefinition {
  eyebrow: string
  name: string
  requiredWins: number
  awardsTitle: boolean
  titlesAwarded?: number
  eliminationOnLoss?: boolean
  description: string
  rounds: readonly [string, ...string[]]
  success: string
  failure: string
  competition?: BossCompetition
}

export type ClubTier = 'second' | 'modest' | 'aspirant' | 'elite'

export type LeaguePrestige = 'emerging' | 'continental' | 'global'

export const CLUB_TIER_LABELS: Readonly<Record<ClubTier, string>> = {
  second: 'Segunda división',
  modest: 'Club modesto',
  aspirant: 'Club aspirante',
  elite: 'Club de élite',
}

export const LEAGUE_PRESTIGE_LABELS: Readonly<Record<LeaguePrestige, string>> = {
  emerging: 'Liga emergente',
  continental: 'Liga continental',
  global: 'Élite mundial',
}

/**
 * Clubes reconocidos como élite deportiva para las decisiones de carrera.
 * La lista es deliberadamente corta: nunca supera tres clubes por liga.
 */
export const ELITE_CLUBS_BY_COUNTRY: Readonly<Record<string, readonly string[]>> = {
  España: ['Real Madrid', 'Barcelona', 'Atlético Madrid'],
  Inglaterra: ['Manchester City', 'Liverpool', 'Arsenal'],
  Italia: ['Inter Milan', 'AC Milan', 'Juventus'],
  Alemania: ['Bayern Munich', 'Borussia Dortmund', 'Bayer Leverkusen'],
  Francia: ['Paris Saint-Germain', 'Marseille', 'Monaco'],
  Argentina: ['Boca Juniors', 'River Plate', 'Racing Club'],
  Brasil: ['Flamengo', 'Palmeiras', 'Corinthians'],
  Uruguay: ['Nacional', 'Peñarol'],
  'Estados Unidos': ['Inter Miami CF', 'LAFC', 'Seattle Sounders FC'],
  'Arabia Saudí': ['Al-Hilal', 'Al-Nassr', 'Al-Ittihad'],
}

const ELITE_CLUB_NAMES = new Set(Object.values(ELITE_CLUBS_BY_COUNTRY).flat())

const CLUB_TIER_RANK: Readonly<Record<ClubTier, number>> = {
  second: 0,
  modest: 1,
  aspirant: 2,
  elite: 3,
}

const LEAGUE_PRESTIGE_BY_LEAGUE: Readonly<Record<string, LeaguePrestige>> = {
  'LALIGA EA SPORTS': 'global',
  'Premier League': 'global',
  'Serie A': 'global',
  Bundesliga: 'global',
  'Ligue 1': 'global',
  'Liga Profesional': 'continental',
  'Brasileirão Série A': 'continental',
}

const LEAGUE_PRESTIGE_RANK: Readonly<Record<LeaguePrestige, number>> = {
  emerging: 0,
  continental: 1,
  global: 2,
}

export interface ClubCompetitiveProfile {
  tier: ClubTier
  leaguePrestige: LeaguePrestige
  level: number
}

export const CLUB_CATALOG: readonly ClubDefinition[] = GENERATED_CLUB_CATALOG

export const RESERVE_TO_SENIOR_CLUB: Readonly<Record<string, string>> = {
  'Celta Fortuna': 'Celta Vigo',
  'Real Sociedad II': 'Real Sociedad',
}

export function getSeniorClubForReserve(clubName: string): ClubDefinition | undefined {
  const seniorName = RESERVE_TO_SENIOR_CLUB[clubName]
  return seniorName ? getClub(seniorName) : undefined
}

export function isReserveClub(clubName: string): boolean {
  return clubName in RESERVE_TO_SENIOR_CLUB
}

export function getClubOrganizationName(clubName: string): string {
  return RESERVE_TO_SENIOR_CLUB[clubName] ?? clubName
}

export const PLAYABLE_COUNTRIES = [
  'España', 'Inglaterra', 'Italia', 'Alemania', 'Francia',
  'Argentina', 'Brasil', 'Uruguay', 'Estados Unidos', 'Arabia Saudí',
] as const

type ClubConfederation = 'UEFA' | 'CONMEBOL' | 'CONCACAF' | 'AFC'

const CLUB_CONFEDERATION_BY_COUNTRY: Readonly<Record<string, ClubConfederation>> = {
  España: 'UEFA',
  Inglaterra: 'UEFA',
  Italia: 'UEFA',
  Alemania: 'UEFA',
  Francia: 'UEFA',
  Argentina: 'CONMEBOL',
  Brasil: 'CONMEBOL',
  Uruguay: 'CONMEBOL',
  'Estados Unidos': 'CONCACAF',
  'Arabia Saudí': 'AFC',
}

export const COUNTRY_FLAGS: Readonly<Record<string, string>> = {
  España: '/assets/flags/es.svg',
  Inglaterra: '/assets/flags/en.svg',
  Italia: '/assets/flags/it.svg',
  Alemania: '/assets/flags/de.svg',
  Francia: '/assets/flags/fr.svg',
  Argentina: '/assets/flags/ar.svg',
  Brasil: '/assets/flags/br.svg',
  Uruguay: '/assets/flags/uy.svg',
  'Estados Unidos': '/assets/flags/us.svg',
  'Arabia Saudí': '/assets/flags/sa.svg',
}

const CLUB_NAME_ALIASES: Readonly<Record<string, string>> = {
  'Athletic Club': 'Athletic Bilbao',
  'Atlético de Madrid': 'Atlético Madrid',
  'CA Osasuna': 'Osasuna',
  'Celta': 'Celta Vigo',
  'FC Barcelona': 'Barcelona',
  'Getafe CF': 'Getafe',
  'RCD Espanyol de Barcelona': 'Espanyol',
  'RC Deportivo': 'Deportivo de A Coruña',
  'Rayo Vallecano de Madrid': 'Rayo Vallecano',
  'Internazionale': 'Inter Milan',
  'Milan': 'AC Milan',
  'Bayern München': 'Bayern Munich',
  'Al Diraiyah': 'Al-Diriyah',
  'Al Najma Unaizah': 'Al-Najmah',
}

function normalizeClubName(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es').replace(/[^a-z0-9]+/g, ' ').trim()
}

const CLUBS_BY_NORMALIZED_NAME = new Map(CLUB_CATALOG.map((club) => [normalizeClubName(club.name), club]))

/**
 * Rivalidades que pueden disputarse con la composición de ligas incluida en el juego.
 * Las parejas son bidireccionales y pueden solaparse para ofrecer más de un rival.
 * `getDerbyRivals` vuelve a comprobar la liga para que una futura actualización
 * del catálogo nunca produzca un derbi entre divisiones.
 */
const DOMESTIC_RIVALRY_PAIRS: readonly (readonly [string, string])[] = [
  // España
  ['Real Madrid', 'Atlético Madrid'], ['Real Madrid', 'Barcelona'], ['Barcelona', 'Espanyol'],
  ['Sevilla', 'Real Betis'], ['Athletic Bilbao', 'Real Sociedad'], ['Valencia', 'Levante'],
  ['Celta Vigo', 'Deportivo de A Coruña'], ['Athletic Bilbao', 'Osasuna'],
  ['Real Oviedo', 'Sporting Gijón'], ['Tenerife', 'Las Palmas'], ['Granada', 'Almería'],
  ['Real Valladolid', 'Burgos'], ['Cádiz', 'Córdoba'], ['Girona', 'CD Sabadell'],

  // Inglaterra
  ['Arsenal', 'Tottenham Hotspur'], ['Liverpool', 'Everton'], ['Manchester City', 'Manchester United'],
  ['Newcastle United', 'Sunderland'], ['Chelsea', 'Fulham'], ['Chelsea', 'Brentford'],
  ['Crystal Palace', 'Brighton and Hove Albion'], ['Aston Villa', 'Coventry City'],
  ['Leeds United', 'Manchester United'], ['Blackburn Rovers', 'Burnley'],
  ['Bolton Wanderers', 'Preston North End'], ['Cardiff City', 'Swansea City'],
  ['Millwall', 'West Ham United'], ['Millwall', 'Charlton Athletic'],
  ['Portsmouth', 'Southampton'], ['Birmingham City', 'West Bromwich Albion'],
  ['Birmingham City', 'Wolverhampton Wanderers'], ['Stoke City', 'West Bromwich Albion'],

  // Italia
  ['AC Milan', 'Inter Milan'], ['AS Roma', 'Lazio'], ['Juventus', 'Torino'],
  ['Juventus', 'Inter Milan'], ['Fiorentina', 'Juventus'], ['Napoli', 'AS Roma'],
  ['Bologna', 'Parma'], ['Bologna', 'Fiorentina'], ['Como', 'Monza'],
  ['Hellas Verona', 'Vicenza'], ['Pisa', 'Empoli'], ['Modena', 'Mantova'],
  ['Avellino', 'Benevento'], ['Palermo', 'Catanzaro'], ['Cesena', 'Carrarese'],

  // Alemania
  ['Bayern Munich', 'Borussia Dortmund'], ['Borussia Dortmund', 'Schalke 04'],
  ['Hamburg', 'Werder Bremen'], ['FC Cologne', 'Borussia Mönchengladbach'],
  ['FC Cologne', 'Bayer Leverkusen'], ['Eintracht Frankfurt', 'Mainz'],
  ['Bayern Munich', 'Augsburg'], ['VfB Stuttgart', 'Freiburg'],
  ['1. FC Nürnberg', 'Greuther Fürth'], ['Hannover 96', 'TSV Eintracht Braunschweig'],
  ['Dynamo Dresden', '1. FC Magdeburg'], ['Arminia Bielefeld', 'VfL Osnabruck'],
  ['Karlsruher SC', 'Kaiserslautern'], ['VfL Wolfsburg', 'TSV Eintracht Braunschweig'],

  // Francia
  ['Paris Saint-Germain', 'Paris FC'], ['Paris Saint-Germain', 'Marseille'],
  ['Marseille', 'Lyon'], ['Lens', 'Lille'], ['Monaco', 'Nice'],
  ['Lorient', 'Stade Rennais'], ['Auxerre', 'Troyes'], ['Le Havre', 'Paris FC'],
  ['AS Nancy Lorraine', 'Metz'], ['Nantes', 'Saint-Étienne'],
  ['Dijon FCO', 'Sochaux'], ['Dunkerque', 'Boulogne'],

  // Argentina
  ['Boca Juniors', 'River Plate'], ['Independiente', 'Racing Club'],
  ['San Lorenzo', 'Huracán'], ['Estudiantes de La Plata', 'Gimnasia y Esgrima La Plata'],
  ["Newell's Old Boys", 'Rosario Central'], ['Belgrano', 'Talleres (Córdoba)'],
  ['Belgrano', 'Instituto'], ['Talleres (Córdoba)', 'Instituto'], ['Banfield', 'Lanús'],
  ['Tigre', 'Platense'], ['Argentinos Juniors', 'Platense'],
  ['Independiente Rivadavia', 'Gimnasia y Esgrima Mendoza'],
  ['All Boys', 'Nueva Chicago'], ['Almagro', 'Estudiantes (Buenos Aires)'],
  ['Atlanta', 'Chacarita Juniors'], ['Almirante Brown', 'Deportivo Morón'],
  ['Temperley', 'Los Andes'], ['Acassuso', 'Colegiales'], ['Colón', 'Atlético de Rafaela'],
  ['San Martín de San Juan', 'Godoy Cruz Antonio Tomba'],

  // Brasil
  ['Flamengo', 'Fluminense'], ['Flamengo', 'Vasco da Gama'], ['Flamengo', 'Botafogo'],
  ['Fluminense', 'Vasco da Gama'], ['Vasco da Gama', 'Botafogo'],
  ['Corinthians', 'Palmeiras'], ['Corinthians', 'São Paulo'], ['Corinthians', 'Santos'],
  ['Palmeiras', 'São Paulo'], ['Santos', 'São Paulo'], ['Grêmio', 'Internacional'],
  ['Atlético Mineiro', 'Cruzeiro'], ['Athletico Paranaense', 'Coritiba'], ['Bahia', 'Vitória'],
  ['Ceará', 'Fortaleza'], ['Goiás', 'Vila Nova'], ['Goiás', 'Atlético Goianiense'],
  ['Vila Nova', 'Atlético Goianiense'], ['Sport Recife', 'Náutico'],
  ['Avaí', 'Criciúma'], ['Londrina', 'Operário Ferroviário'],

  // Uruguay
  ['Nacional', 'Peñarol'], ['Danubio', 'Defensor Sporting'],
  ['Liverpool Montevideo', 'Montevideo Wanderers'], ['Cerro', 'Progreso'],
  ['Boston River', 'Racing Club de Montevideo'], ['Cerrito', 'Rentistas'],
  ['La Luz', 'Uruguay Montevideo'], ['Fénix', 'Miramar Misiones'],

  // Estados Unidos y Canadá
  ['LA Galaxy', 'LAFC'], ['New York City FC', 'New York Red Bulls'],
  ['Portland Timbers', 'Seattle Sounders FC'], ['Portland Timbers', 'Vancouver Whitecaps FC'],
  ['Seattle Sounders FC', 'Vancouver Whitecaps FC'], ['Inter Miami CF', 'Orlando City SC'],
  ['FC Dallas', 'Houston Dynamo FC'], ['FC Dallas', 'Austin FC'], ['Austin FC', 'Houston Dynamo FC'],
  ['Columbus Crew', 'FC Cincinnati'], ['Toronto FC', 'CF Montréal'],
  ['Sporting Kansas City', 'St. Louis CITY SC'], ['Real Salt Lake', 'Colorado Rapids'],
  ['LA Galaxy', 'San Jose Earthquakes'], ['Atlanta United', 'Charlotte FC'],
  ['Nashville SC', 'Atlanta United'], ['DC United', 'New York Red Bulls'],

  // Arabia Saudí
  ['Al-Hilal', 'Al-Nassr'], ['Al-Hilal', 'Al-Shabab'], ['Al-Nassr', 'Al-Shabab'],
  ['Al-Riyadh', 'Al-Hilal'], ['Al-Riyadh', 'Al-Nassr'], ['Al-Ittihad', 'Al-Ahli'],
  ['Al-Ettifaq', 'Al-Qadisiyah'], ['Al-Ettifaq', 'Al-Khaleej'],
  ['Al-Qadisiyah', 'Al-Khaleej'], ['Al-Wehda', 'Jeddah'], ['Al-Raed', 'Al-Najmah'],
  ['Damac', 'Al-Okhdood'], ['Al-Jabalain', 'Al-Tai'],
]

const RIVAL_NAMES_BY_CLUB = new Map<string, string[]>()
for (const [first, second] of DOMESTIC_RIVALRY_PAIRS) {
  RIVAL_NAMES_BY_CLUB.set(first, [...(RIVAL_NAMES_BY_CLUB.get(first) ?? []), second])
  RIVAL_NAMES_BY_CLUB.set(second, [...(RIVAL_NAMES_BY_CLUB.get(second) ?? []), first])
}

export function getInternationalRivals(country: string): readonly string[] {
  return getWorldCup2026Rivals(country)
}

export const CAREER_STAGES = [
  {
    id: 'academy', name: 'Cantera', age: 16,
    description: 'Formarte en el club que elegiste y ganarte un sitio.',
    boss: {
      eyebrow: 'Examen de academia', name: 'El partido de escaparate', requiredWins: getStageBossBalance(1).requiredWins, awardsTitle: false,
      description: 'Un partido ante otra academia para demostrar que mereces llamar a la puerta del primer equipo.',
      rounds: ['Partido de escaparate'], competition: 'domestic',
      success: 'Tu actuación convence al cuerpo técnico: se abre la puerta del primer equipo y llegan ofertas.',
      failure: 'El primer equipo todavía no te considera preparado y la carrera de leyenda termina en la academia.',
    },
  },
  {
    id: 'debut', name: 'Debut profesional', age: 19,
    description: 'Convertir la oportunidad en minutos de verdad.',
    boss: {
      eyebrow: 'Confianza del entrenador', name: 'Dos oportunidades', requiredWins: getStageBossBalance(2).requiredWins, awardsTitle: false,
      description: 'El entrenador te concede dos titularidades. Gana una para dejar de ser una apuesta provisional.',
      rounds: ['Primera titularidad', 'Examen del entrenador'], competition: 'domestic',
      success: 'Dejas de ser una aparición puntual y te conviertes en futbolista del primer equipo.',
      failure: 'No aprovechas las titularidades y pierdes tu sitio en la rotación.',
    },
  },
  {
    id: 'consolidation', name: 'Consolidación', age: 23,
    description: 'Dejar de ser promesa y sostener el rendimiento.',
    boss: {
      eyebrow: 'La liga se decide', name: 'Asalto al campeonato', requiredWins: getStageBossBalance(3).requiredWins, awardsTitle: true,
      description: 'Tres jornadas deciden la liga nacional. Gana al menos dos para conquistar tu primer gran título.',
      rounds: ['Ante el perseguidor', 'La salida más difícil', 'Última jornada'],
      success: 'Conquistas la liga nacional y dejas definitivamente de ser una promesa.',
      failure: 'El campeonato nacional se escapa en las últimas jornadas.',
    },
  },
  {
    id: 'elite', name: 'Salto a la élite', age: 27,
    description: 'Competir bajo presión por los grandes objetivos.',
    boss: {
      eyebrow: 'Noches continentales', name: 'Camino a Europa', requiredWins: getStageBossBalance(4).requiredWins, awardsTitle: true, eliminationOnLoss: true,
      description: 'Cuartos, semifinal y final de la competición continental. Cada eliminatoria es obligatoria para levantar el gran título.',
      rounds: ['Cuartos de final', 'Semifinal', 'La final'], competition: 'continental-cup',
      success: 'Levantas el título continental después de superar cada eliminatoria.',
      failure: 'Europa te aparta del gran título cuando la presión era máxima.',
    },
  },
  {
    id: 'legacy', name: 'Legado', age: 32,
    description: 'Definir cómo será recordada tu carrera.',
    boss: {
      eyebrow: 'Con tu selección', name: 'El Mundial', requiredWins: getStageBossBalance(5).requiredWins, awardsTitle: true, eliminationOnLoss: true,
      description: 'Cuartos, semifinal y final con tu país. Tres victorias separan una gran carrera de la eternidad.',
      rounds: ['Cuartos del Mundial', 'Semifinal del Mundial', 'Final del Mundial'], competition: 'world-cup',
      success: 'Levantas el Mundial con tu selección y completas una carrera para la historia.',
      failure: 'El Mundial se escapa y la carrera termina sin el último gran trofeo.',
    },
  },
] as const

const TREBLE_FINALE_STAGE = {
  id: 'legacy', name: 'Legado', age: 32,
  description: 'Cerrar tu carrera intentando conquistar todos los grandes títulos de la temporada.',
  boss: {
    eyebrow: 'La temporada perfecta', name: 'El Triplete', requiredWins: getStageBossBalance(5).requiredWins, awardsTitle: true, titlesAwarded: 3,
    description: 'Liga, copa y competición continental. Gana las tres finales para cerrar una carrera histórica con tu club.',
    rounds: ['Final de liga', 'Final de copa', 'Final continental'] as const, competition: 'domestic',
    success: 'Conquistas el Triplete y completas una carrera para la historia.',
    failure: 'La temporada perfecta se escapa antes de completar el Triplete.',
  },
} as const

export function getCareerStage(stage: number, finaleKind: FinaleKind | null = 'world-cup') {
  if (stage >= 5 && finaleKind === 'treble') return TREBLE_FINALE_STAGE
  return CAREER_STAGES[Math.min(Math.max(stage - 1, 0), CAREER_STAGES.length - 1)]!
}

export function getClub(name: string): ClubDefinition | undefined {
  const aliased = CLUB_NAME_ALIASES[name] ?? name
  return CLUBS_BY_NORMALIZED_NAME.get(normalizeClubName(aliased))
}

export function getEffectiveClub(clubName: string, movements: readonly LeagueMovement[] = []): ClubDefinition | undefined {
  const club = getClub(clubName)
  if (!club) return undefined
  const movement = movements.find((entry) => entry.promotedClub === club.name || entry.relegatedClub === club.name)
  if (!movement) return club
  const division: 1 | 2 = movement.promotedClub === club.name ? 1 : 2
  const league = CLUB_CATALOG.find((candidate) => candidate.country === club.country && candidate.division === division)?.league
  return league ? { ...club, division, league } : club
}

export function getClubTier(clubName: string, movements: readonly LeagueMovement[] = []): ClubTier {
  const club = getEffectiveClub(clubName, movements)
  if (!club || club.division === 2) return 'second'
  if (movements.some((movement) => movement.promotedClub === club.name)) return 'modest'
  if (ELITE_CLUB_NAMES.has(club.name)) return 'elite'
  const result = CLUB_SEASON_RESULTS[clubName]
  if (!result) return 'modest'
  if (result.relegated) return 'second'
  if (result.promoted || result.previousDivision === 2) return 'modest'
  if (result.position <= Math.ceil(result.teams / 3)) return 'aspirant'
  return 'modest'
}

export function getClubCompetitiveProfile(clubName: string, movements: readonly LeagueMovement[] = []): ClubCompetitiveProfile {
  const club = getEffectiveClub(clubName, movements)
  const tier = getClubTier(clubName, movements)
  const leaguePrestige = club ? LEAGUE_PRESTIGE_BY_LEAGUE[club.league] ?? 'emerging' : 'emerging'
  return {
    tier,
    leaguePrestige,
    level: CLUB_TIER_RANK[tier] + LEAGUE_PRESTIGE_RANK[leaguePrestige],
  }
}

export function isTitleContender(clubName: string, movements: readonly LeagueMovement[] = []): boolean {
  const tier = getClubTier(clubName, movements)
  return tier === 'aspirant' || tier === 'elite'
}

export function isSouthAmericanClub(clubName: string, movements: readonly LeagueMovement[] = []): boolean {
  const country = getEffectiveClub(clubName, movements)?.country
  return country === 'Argentina' || country === 'Brasil' || country === 'Uruguay'
}

export function getStageBoss(stage: number, clubName: string, movements: readonly LeagueMovement[] = [], finaleKind: FinaleKind | null = 'world-cup'): CareerBossDefinition {
  const base = getCareerStage(stage, finaleKind).boss
  const tier = getClubTier(clubName, movements.filter((movement) => movement.afterStage < stage))
  if (stage === 3) {
    if (tier === 'second') return {
      eyebrow: 'Todo o nada', name: 'Playoff de ascenso', requiredWins: getStageBossBalance(3).requiredWins, awardsTitle: false,
      description: 'Tu club sigue en segunda. Supera la semifinal a doble partido y la final para ascender a primera.',
      rounds: ['Semifinal · ida', 'Semifinal · vuelta', 'Final del playoff'],
      success: 'Llevas al club a primera división y confirmas tu consolidación profesional.',
      failure: 'El ascenso se escapa y tu carrera pierde el impulso necesario.',
    }
    if (tier === 'modest') {
      if (isSouthAmericanClub(clubName, movements)) return {
        eyebrow: 'Objetivo continental', name: 'La carrera continental', requiredWins: getStageBossBalance(3).requiredWins, awardsTitle: false,
        description: 'El club no parte como candidato a la liga. Gana dos de las tres últimas jornadas para clasificarlo a una competición continental.',
        rounds: ['Duelo por la plaza', 'Visita decisiva', 'Última jornada'],
        success: 'Clasificas al club para una competición continental y demuestras que puedes sostener un proyecto ambicioso.',
        failure: 'La plaza continental se escapa en la recta final.', competition: 'continental-qualification',
      }
      return {
        eyebrow: 'Objetivo europeo', name: 'La carrera por Europa', requiredWins: getStageBossBalance(3).requiredWins, awardsTitle: false,
        description: 'El club no parte como candidato a la liga. Gana dos de las tres últimas jornadas para clasificarlo a una competición continental.',
        rounds: ['Duelo por la plaza', 'Visita decisiva', 'Última jornada'],
        success: 'Clasificas al club para Europa y demuestras que puedes sostener un proyecto ambicioso.',
        failure: 'La plaza continental se escapa en la recta final.', competition: 'continental-qualification',
      }
    }
  }
  if (stage === 4 && tier === 'second') return {
    eyebrow: 'Después del ascenso', name: 'La copa nacional', requiredWins: getStageBossBalance(4).requiredWins, awardsTitle: true, eliminationOnLoss: true,
    description: 'El club afronta su primera campaña tras subir a primera. Supera cuartos, semifinal y final para confirmar que puede competir con la élite.',
    rounds: ['Cuartos de copa', 'Semifinal de copa', 'Final de copa'],
    success: 'Conquistas la copa nacional en la primera temporada del club tras el ascenso.',
    failure: 'La copa se escapa, aunque el club ya compite en primera gracias al ascenso anterior.', competition: 'national-cup',
  }
  if (stage === 4 && tier === 'modest') return {
    eyebrow: 'La ruta del aspirante', name: 'Clasificación nacional', requiredWins: getStageBossBalance(4).requiredWins, awardsTitle: true, eliminationOnLoss: true,
    description: 'El club todavía no está preparado para dominar su continente. Supera los cuartos, la semifinal y la final de la copa nacional para completar el salto competitivo.',
    rounds: ['Cuartos de la copa nacional', 'Semifinal de la copa nacional', 'Final de la copa nacional'],
    success: 'Conquistas la copa nacional y conviertes a tu club en un nombre respetado.',
    failure: 'La copa nacional se escapa antes de completar el salto competitivo.', competition: 'national-qualification',
  }
  if (stage === 4 && (tier === 'aspirant' || tier === 'elite') && isSouthAmericanClub(clubName, movements)) return {
    eyebrow: 'Noches continentales', name: 'La competición continental', requiredWins: getStageBossBalance(4).requiredWins, awardsTitle: true, eliminationOnLoss: true,
    description: 'Cuartos, semifinal y final de la competición continental. Cada eliminatoria es obligatoria para levantar el gran título.',
    rounds: ['Cuartos de final', 'Semifinal', 'La final'],
    success: 'Levantas el título continental después de superar cada eliminatoria.',
    failure: 'La competición continental te aparta del gran título cuando la presión era máxima.', competition: 'continental-cup',
  }
  return base
}

export function getDecisiveBossScores(
  boss: Pick<CareerBossDefinition, 'requiredWins' | 'rounds' | 'eliminationOnLoss'>,
  scores: readonly BossState['scores'][number][],
): BossState['scores'] {
  const recordedScores = scores.slice(0, boss.rounds.length)
  let wins = 0

  for (let index = 0; index < recordedScores.length; index += 1) {
    const score = recordedScores[index]
    if (score === 'win') wins += 1
    const playedRounds = index + 1
    const remainingRounds = boss.rounds.length - playedRounds
    const isDecisive = wins >= boss.requiredWins
      || wins + remainingRounds < boss.requiredWins
      || Boolean(boss.eliminationOnLoss && score === 'loss')

    if (isDecisive) return recordedScores.slice(0, playedRounds)
  }

  return recordedScores
}

export function isBossSeriesFinished(stage: number, clubName: string, boss: BossState | null, movements: readonly LeagueMovement[] = [], finaleKind: FinaleKind | null = 'world-cup'): boolean {
  if (!boss) return false
  const stageBoss = getStageBoss(stage, clubName, movements, finaleKind)
  const remainingRounds = Math.max(0, stageBoss.rounds.length - boss.round)
  if (boss.wins >= stageBoss.requiredWins) return true
  if (boss.wins + remainingRounds < stageBoss.requiredWins) return true
  if (boss.round >= stageBoss.rounds.length) return true
  return Boolean(stageBoss.eliminationOnLoss && boss.scores.at(-1) === 'loss')
}

export function createPromotionMovement(
  promotedClubName: string,
  movements: readonly LeagueMovement[] = [],
  random: () => number = Math.random,
): LeagueMovement | null {
  const promotedClub = getEffectiveClub(promotedClubName, movements)
  if (!promotedClub || promotedClub.division !== 2) return null
  const firstDivision = CLUB_CATALOG
    .map((club) => getEffectiveClub(club.name, movements)!)
    .filter((club) => club.country === promotedClub.country && club.division === 1 && club.name !== promotedClub.name)
    .sort((first, second) => {
      const weakness = (club: ClubDefinition) => {
        const result = CLUB_SEASON_RESULTS[club.name]
        if (!result) return .75
        return (result.promoted || result.previousDivision === 2 ? 1 : 0) + result.position / Math.max(1, result.teams)
      }
      return weakness(second) - weakness(first)
    })
  if (!firstDivision.length) return null
  const weakestPool = firstDivision.slice(0, Math.max(3, Math.ceil(firstDivision.length / 4)))
  const relegatedClub = weakestPool[Math.floor(random() * weakestPool.length)] ?? weakestPool[0]
  if (!relegatedClub) return null
  return { promotedClub: promotedClub.name, relegatedClub: relegatedClub.name, country: promotedClub.country, afterStage: 3 }
}

export function getCountryClubs(country: string): readonly ClubDefinition[] {
  return CLUB_CATALOG.filter((club) => club.country === country)
}

function randomItem<T>(items: readonly T[], random: () => number): T | undefined {
  return items[Math.floor(random() * items.length)]
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const result = [...items]
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1))
    ;[result[index], result[other]] = [result[other]!, result[index]!]
  }
  return result
}

/** Genera tres ofertas y, cuando existen dos categorías, incluye al menos un club de cada una. */
export function getRandomStartingClubs(country: string, random: () => number = Math.random): readonly ClubDefinition[] {
  const pool = getCountryClubs(country)
  const first = pool.filter((club) => club.division === 1)
  const second = pool.filter((club) => club.division === 2)
  const picked: ClubDefinition[] = []
  const add = (club?: ClubDefinition) => { if (club && !picked.some((item) => item.name === club.name)) picked.push(club) }

  add(randomItem(first, random))
  add(randomItem(second, random))
  for (const club of shuffle(pool, random)) {
    add(club)
    if (picked.length === 3) break
  }
  return shuffle(picked, random)
}

/** Fallback estable para migraciones antiguas y tests; la UI siempre pasa el club elegido. */
export function getStartingClubs(country: string): readonly ClubDefinition[] {
  return getCountryClubs(country).slice(0, 3)
}

export function getLeagueRivals(clubName: string, movements: readonly LeagueMovement[] = []): readonly ClubDefinition[] {
  const current = getEffectiveClub(clubName, movements)
  if (!current) return []
  const rivals = CLUB_CATALOG.map((club) => getEffectiveClub(club.name, movements)!).filter((club) => club.league === current.league && club.id !== current.id)
  return rivals
}

function getContinentalEliteRivals(clubName: string, movements: readonly LeagueMovement[] = []): readonly ClubDefinition[] {
  const current = getEffectiveClub(clubName, movements)
  if (!current) return []
  const confederation = CLUB_CONFEDERATION_BY_COUNTRY[current.country]
  const foreignElite = CLUB_CATALOG
    .map((club) => getEffectiveClub(club.name, movements)!)
    .filter((club) => club.division === 1
      && club.id !== current.id
      && club.country !== current.country
      && getClubTier(club.name, movements) === 'elite')
  const sameConfederation = foreignElite.filter((club) => CLUB_CONFEDERATION_BY_COUNTRY[club.country] === confederation)
  return sameConfederation.length ? sameConfederation : foreignElite
}

/**
 * Mantiene domésticas las rondas nacionales y reserva los cruces continentales
 * para clubes extranjeros de élite de la misma confederación cuando el catálogo
 * dispone de ellos.
 */
export function getClubBossRivals(
  stage: number,
  clubName: string,
  round: number,
  movements: readonly LeagueMovement[] = [],
  finaleKind: FinaleKind | null = 'world-cup',
): readonly ClubDefinition[] {
  const boss = getStageBoss(stage, clubName, movements, finaleKind)
  const isContinentalRound = boss.competition === 'continental-cup'
    || (stage >= 5 && finaleKind === 'treble' && boss.rounds[round] === 'Final continental')
  return isContinentalRound ? getContinentalEliteRivals(clubName, movements) : getLeagueRivals(clubName, movements)
}

/** Devuelve únicamente rivalidades configuradas que siguen compartiendo liga. */
export function getDerbyRivals(clubName: string, movements: readonly LeagueMovement[] = []): readonly ClubDefinition[] {
  const current = getEffectiveClub(clubName, movements)
  if (!current) return []
  const configuredNames = RIVAL_NAMES_BY_CLUB.get(current.name) ?? []
  return configuredNames
    .map((name) => getEffectiveClub(name, movements))
    .filter((club): club is ClubDefinition => Boolean(club && club.league === current.league && club.id !== current.id))
}

/** Usa cinco rivales distintos dentro del tramo siempre que la liga tenga suficientes clubes. */
export function getLeagueBlockFixtureCount(availableRivals: number, _step: number): number {
  if (availableRivals <= 0) return 0
  return Math.min(5, availableRivals)
}

const LEAGUE_MATCHDAYS: Readonly<Record<string, number>> = {
  'LALIGA EA SPORTS': 38,
  'LALIGA HYPERMOTION': 42,
  'Premier League': 38,
  Championship: 46,
  'Serie A': 38,
  'Serie B': 38,
  Bundesliga: 34,
  '2. Bundesliga': 34,
  'Ligue 1': 34,
  'Ligue 2': 34,
  'Liga Profesional': 30,
  'Primera Nacional': 34,
  'Brasileirão Série A': 38,
  'Brasileirão Série B': 38,
  'Primera División': 30,
  'Segunda División Profesional': 26,
  MLS: 34,
  'Saudi Pro League': 34,
  'Saudi First Division': 34,
}

export function getLeagueSchedule(clubName: string, step: number, movements: readonly LeagueMovement[] = []): { startMatchday: number; totalMatchdays: number } {
  const league = getEffectiveClub(clubName, movements)?.league ?? ''
  const totalMatchdays = LEAGUE_MATCHDAYS[league] ?? 38
  const normalizedStep = Math.max(0, Math.min(4, Math.floor(step)))
  const startMatchday = 1 + Math.round(normalizedStep * (totalMatchdays - 5) / 4)
  return { startMatchday, totalMatchdays }
}

/**
 * Las propuestas acompañan la curva de carrera. Una llamada temprana de un
 * gigante sigue siendo posible, pero no convierte la primera temporada en una
 * ruta de dificultad menor por haber empezado ya en un club de élite.
 */
export function getTransferOfferTiers(completedStage: number, random: () => number = Math.random): readonly ClubTier[] {
  if (completedStage <= 1) return random() < .15 ? ['elite'] : ['modest']
  if (completedStage === 2) return random() < .15 ? ['elite'] : ['aspirant']
  if (completedStage === 3) return ['aspirant', 'elite']
  return ['elite']
}

export function getTransferOffers(
  country: string,
  _completedStage: number,
  currentClub: string,
  count = 2,
  random: () => number = Math.random,
  movements: readonly LeagueMovement[] = [],
): readonly ClubDefinition[] {
  const current = getEffectiveClub(currentClub, movements)
  const currentCountry = current?.country ?? country
  const foreignFirstDivision = CLUB_CATALOG
    .map((club) => getEffectiveClub(club.name, movements)!)
    .filter((club) => !isReserveClub(club.name)
      && club.division === 1
      && club.name !== currentClub
      && club.country !== currentCountry)
  const preferredTiers = getTransferOfferTiers(_completedStage, random)
  const preferred = foreignFirstDivision.filter((club) => preferredTiers.includes(getClubTier(club.name, movements)))
  const fallback = foreignFirstDivision.filter((club) => !preferred.includes(club))
  const shuffled = [...shuffle(preferred, random), ...shuffle(fallback, random)]
  const offers: ClubDefinition[] = []
  const first = shuffled[0]
  if (first) offers.push(first)
  const differentCountry = shuffled.find((club) => !offers.some((offer) => offer.name === club.name) && club.country !== first?.country)
  if (differentCountry && offers.length < count) offers.push(differentCountry)
  for (const club of shuffled) {
    if (offers.length >= count) break
    if (!offers.some((offer) => offer.name === club.name)) offers.push(club)
  }
  return offers
}

export function getTransferOffer(country: string, completedStage: number, currentClub: string): ClubDefinition {
  return getTransferOffers(country, completedStage, currentClub, 1)[0]
    ?? CLUB_CATALOG.find((club) => club.division === 1 && !isReserveClub(club.name))!
}
